// @flow

import * as React from 'react';

import CopyToClipboard from 'react-copy-to-clipboard';
import { useDispatch } from 'react-redux';
import { superpathjoin } from 'superpathjoin';

import { selectCartAffiliate } from '@gandi/cart';
import { notifySuccess } from '@gandi/notifications';
import { selectFromConfig } from '@gandi/redux-config';
import { selectPersonalId } from '@gandi/redux-sharing';
import { useTranslator } from '@gandi/translate';
import {
  Base,
  Button,
  OrderedList,
  TypoListHelp,
  TypoListItem,
  TypoListTitle,
} from '@gandi/ui';
import { ListRow, ListItem, TitleCell } from '@gandi/ui/deprecated';

import { InputText } from 'gandi.react-form';

import { useNotifications, useAppSelector } from 'src/hooks';

import { selectShopURL } from '../../../modules/config';

import type { FullState, PreFetchArgs, PreFetchReturn } from '../../../types';
import {
  selectAffiliateId,
  fetchAffiliateLink,
  postAffiliateLink,
  referResource,
} from '../../modules/affiliate';

import styles from './Refer.scss';

const selectlinkProdRaF = selectFromConfig<string>(
  'app.layout.urls.link_raf',
  '',
);

export function Refer(): null | React.Node {
  const codeID = useAppSelector(selectAffiliateId);
  const isLoaded = useAppSelector(referResource.selectIsLoaded);
  const isFetching = useAppSelector(referResource.selectIsFetching);
  const isFailure = useAppSelector(referResource.selectIsFailure);
  const linkRaF = useAppSelector(selectlinkProdRaF);
  const userId = useAppSelector(selectPersonalId);
  const shopUrl = useAppSelector(selectShopURL);
  const affiliate = useAppSelector(selectCartAffiliate);

  const dispatch = useDispatch();

  const { __, formatPrice } = useTranslator();
  const { notifyMaybeApiError } = useNotifications();

  // $FlowFixMe[incompatible-call]
  const reward = formatPrice('5', 'EUR');
  // $FlowFixMe[incompatible-call]
  const purchaseAmount = formatPrice('3', 'EUR');

  const urlToRefer = linkRaF
    ? superpathjoin(linkRaF, codeID)
    : superpathjoin(shopUrl, 'domain', `suggest?ah=${codeID}`);
  const onCopy = () => {
    dispatch(notifySuccess(__('The refer link has been copied')));
  };

  const handlePostAffiliateLink = () => {
    dispatch(postAffiliateLink(userId)).then(
      () => {
        dispatch(fetchAffiliateLink(userId));
        dispatch(notifySuccess(__('A link has been generated')));
      },
      error => {
        dispatch(notifyMaybeApiError(error));
        throw error;
      },
    );
  };

  React.useEffect(() => {
    if (
      !isLoaded &&
      !isFailure &&
      !isFetching &&
      userId &&
      affiliate === 'FRIEND'
    ) {
      dispatch(fetchAffiliateLink(userId)).catch(error => {
        dispatch(notifyMaybeApiError(error));
      });
    }
  }, [
    dispatch,
    isLoaded,
    isFailure,
    isFetching,
    userId,
    notifyMaybeApiError,
    affiliate,
  ]);

  if (affiliate !== 'FRIEND' || !userId) {
    return null;
  }

  return (
    <Base marginBottom="base">
      <ListRow separatorBottom>
        <ListItem>
          <TitleCell icon="gift" iconTheme="green">
            {__('Give your friends discounts and get rewarded')}
          </TitleCell>
        </ListItem>
      </ListRow>
      <Base padding="small" backgroundColor="surface" marginBottom="base">
        <Base tagName="p" marginTop="none">
          {__(
            'Treat your friends! Give them 75%% off web hosting and 20%% off their domain name and get ' +
              'a %(reward)s* promo code when they complete their first order of %(purchaseAmount)s* or more as a new Gandi customer—for each time ' +
              'you refer a friend or family member.',
            {
              reward,
              purchaseAmount,
            },
          )}
        </Base>
        <Base tagName="p" marginTop="none" marginBottom="base">
          {__(
            "On the third referral, we'll send you a collector Gandi t-shirt!",
          )}{' '}
          <a
            href={__(
              'https://docs.gandi.net/en/billing/means_of_payment/refer_a_friend.html',
            )}
            target="_blank"
            rel="noopener noreferrer"
          >
            {__('Learn more about the program')}
          </a>
        </Base>
        <OrderedList>
          <TypoListItem>
            <TypoListTitle marginBottom="small" size="base">
              {__(
                'Share your referral link with your friends to give them 75%% off a Simple Hosting S+ pack and 20%% off the purchase or transfer of a domain name.',
              )}
            </TypoListTitle>
            <TypoListHelp tagName="div">
              {codeID ? (
                <Base flex>
                  <Base marginRight="xsmall">
                    <InputText
                      readOnly
                      value={urlToRefer}
                      className={styles.urlToCopy}
                    />
                  </Base>
                  <CopyToClipboard text={urlToRefer} onCopy={onCopy}>
                    <Button type="button">{__('Copy')}</Button>
                  </CopyToClipboard>
                </Base>
              ) : (
                <Button
                  type="button"
                  loading={(isFetching || !isLoaded) && !isFailure}
                  onClick={handlePostAffiliateLink}
                >
                  {__('Generate link')}
                </Button>
              )}
            </TypoListHelp>
          </TypoListItem>
          <TypoListItem>
            <TypoListTitle size="base">
              {__(
                'Receive a %(reward)s* promo code when they create a new account and complete their first order ' +
                  'greater than or equal to %(purchaseAmount)s* via the referral link.',
                {
                  reward,
                  purchaseAmount,
                },
              )}
            </TypoListTitle>
            <TypoListHelp color="light">
              {__(
                '* Or equivalent value in your local currency, valid on any order greater than or equal to this same amount, ' +
                  'one code per order, excluding all current promos.',
              )}
            </TypoListHelp>
          </TypoListItem>
        </OrderedList>
      </Base>
    </Base>
  );
}

Refer.preFetch = ({
  dispatch,
  getState,
}: PreFetchArgs<{}, void, empty, FullState>): PreFetchReturn<void> => {
  const userId = selectPersonalId(getState());

  return userId ? dispatch(fetchAffiliateLink(userId)) : Promise.resolve();
};

export default Refer;
